import {CouponType, CouponValueType} from "../../Enums/Coupons";
import {Coupon} from "../../Types/Coupons";

/**
 * Returns a message corresponding to the coupon type.
 *
 * @param {Coupon} coupon - The coupon object.
 * @param {CouponType} coupon.couponType - The type of the coupon.
 * @param {CouponValueType} [coupon.couponValueType] - The value type of the coupon (optional).
 * @returns {string|null} The message corresponding to the coupon type, or null if the type is not found.
 */
export const getCouponType = (coupon: Coupon): string | null => {
    const mapping = {
        [CouponType.Sponsorship]: 'parrainage', [CouponType.Luncheon]: 'titre restaurant',
        [CouponType.Participation]: 'carte de participation', [CouponType.Refund]: 'avoir',
        [CouponType.Fidelity]: 'fidélité',
        [CouponType.Voucher]: coupon.couponValueType === CouponValueType.Product ? 'ajout produit' : 'bon de réduction',
    };
    return coupon.couponType ? mapping[coupon.couponType] : null;
};


/**
 * Returns an array of forbidden coupon types for a given coupon type.
 *
 * @param {CouponType} type - The type of the coupon.
 * @returns {CouponType[]} An array of forbidden coupon types.
 */
export const getForbiddenTypes = (type: CouponType): CouponType[] => {
    const mapping: { [key in CouponType]: CouponType[] } = {
        [CouponType.Sponsorship]: [
            CouponType.Voucher,
            CouponType.Sponsorship
        ], [CouponType.Voucher]: [CouponType.Sponsorship], [CouponType.Luncheon]: [], [CouponType.Fidelity]: [],
        [CouponType.Refund]: [], [CouponType.Participation]: [],
    };
    return mapping[type] || [];
};

/**
 * Groups coupons by their type and returns an array of grouped coupons.
 *
 * @param {Coupon[]} coupons - The array of coupon objects.
 * @param {number} coupons[].id - The ID of the coupon.
 * @param {CouponType} coupons[].couponType - The type of the coupon.
 * @returns {Object[]} An array of grouped coupons with titles, legends, and coupons.
 */
export const getCouponsGroupedByType = (coupons: Coupon[]) => {
    const sortedCoupons = coupons.sort((a, b) => a.id - b.id);
    const groupByType = (type: CouponType, title: string, legend?: string) => ({
        title, legend, coupons: sortedCoupons.filter(c => c.couponType === type),
    });

    return [
        groupByType(CouponType.Luncheon, 'Titres restaurant', 'Vous pouvez utiliser jusqu\'à 2 titres restaurant par commande.'),
        groupByType(CouponType.Participation, 'Cartes de participation'),
        groupByType(CouponType.Refund, 'Avoirs'),
        {
            title: 'Bons de réduction et parrainage',
            legend: 'Ces coupons ne sont pas cumulables à l\'exception des bons d\'achat Potager City. Faites votre choix parmi vos coupons disponibles :',
            coupons: sortedCoupons.filter(c => [
                CouponType.Sponsorship,
                CouponType.Voucher
            ].includes(c.couponType)),
        },
        groupByType(CouponType.Fidelity, 'Fidélité'),
    ];
};
